import React from 'react';
import { Box, Typography, IconButton, Tooltip, Collapse, Button, CircularProgress, Chip } from '@mui/material';
import {
  Close,
  ErrorOutline,
  WarningAmber,
  InfoOutlined,
  TrendingUp,
  ChevronRight,
  AutoAwesome,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { aiService, Insight } from '../services/aiService';
import { useChartTokens } from './chartTokens';
import { useAssistantAvailable } from '../ai/AssistantPanel';

/**
 * "What needs my attention" — the strip above the grid.
 *
 * These are computed in SQL server-side, not written by a model, and each
 * carries the numbers it was derived from. That is why the evidence is shown
 * rather than hidden behind a tooltip: in a finance product a claim like
 * "payables exceed receivables by ₹4.2L" is something a person will act on,
 * and an assertion they cannot check is worse than no assertion.
 *
 * Renders with no AI credentials configured — the rules do not need a model.
 */
const SEVERITY_META = {
  CRITICAL: { icon: ErrorOutline, role: 'critical' as const },
  WARNING: { icon: WarningAmber, role: 'warning' as const },
  POSITIVE: { icon: TrendingUp, role: 'good' as const },
  INFO: { icon: InfoOutlined, role: 'serious' as const },
};

const InsightStrip: React.FC = () => {
  const t = useChartTokens();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const aiAvailable = useAssistantAvailable();

  const { data } = useQuery<{ insights: Insight[]; count: number; critical: number }>({
    queryKey: ['insights'],
    queryFn: () => aiService.insights(),
    staleTime: 2 * 60_000,
  });

  const dismiss = useMutation({
    mutationFn: (id: number) => aiService.dismissInsight(id),
    // Optimistic: the card disappears immediately. Dismissing is reversible
    // server-side and low stakes, so waiting on a round trip just feels slow.
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['insights'] });
      const prev = qc.getQueryData<{ insights: Insight[] }>(['insights']);
      qc.setQueryData(['insights'], (old: any) => ({
        ...old,
        insights: (old?.insights ?? []).filter((i: Insight) => i.id !== id),
      }));
      return { prev };
    },
    onError: (_e, _id, ctx: any) => {
      if (ctx?.prev) qc.setQueryData(['insights'], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['insights'] }),
  });

  const insights: Insight[] = data?.insights ?? [];

  // Model insights first — they are the analysis, the rules are the facts.
  const ordered = React.useMemo(
    () => [...insights].sort((a, b) => Number(b.source === 'MODEL') - Number(a.source === 'MODEL')),
    [insights]
  );

  const generate = useMutation({
    mutationFn: () => aiService.generateInsights(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['insights'] }),
  });

  // Nothing wrong is a valid state and needs no chrome, but keep the analyse
  // action reachable — otherwise a clean workspace can never ask for one.
  if (ordered.length === 0 && !aiAvailable) return null;

  return (
    <Collapse in>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}
        >
          Needs your attention
        </Typography>

        {/* Only offered when credentials exist. Rule insights above are
            unaffected by this button and appear regardless. */}
        {aiAvailable && (
          <Button
            size="small"
            startIcon={
              generate.isPending ? <CircularProgress size={12} /> : <AutoAwesome sx={{ fontSize: 14 }} />
            }
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
            sx={{ fontSize: '0.75rem', py: 0.25, textTransform: 'none' }}
          >
            {generate.isPending ? 'Analysing…' : 'Analyse with AI'}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          mb: 2.5,
          overflowX: 'auto',
          pb: 0.5,
          // Wide content scrolls inside its own container; the page never does.
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: t.grid, borderRadius: 3 },
        }}
      >
        {ordered.map((insight) => {
          const meta = SEVERITY_META[insight.severity] ?? SEVERITY_META.INFO;
          const Icon = meta.icon;
          const color = t.status[meta.role];
          const evidence = insight.evidence as Record<string, unknown> | null;

          return (
            <Box
              key={insight.id}
              sx={{
                position: 'relative',
                minWidth: 290,
                maxWidth: 360,
                flexShrink: 0,
                p: 1.75,
                pr: 4,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                // Severity carries a coloured rail AND an icon — never colour alone.
                borderLeft: `3px solid ${color}`,
                backgroundColor: 'background.paper',
                cursor: insight.link ? 'pointer' : 'default',
                transition: 'border-color .15s ease',
                '&:hover': insight.link ? { borderColor: color } : {},
              }}
              onClick={() => insight.link && navigate(insight.link)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Icon sx={{ fontSize: 17, color, mt: '1px', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  {/* Provenance is not decoration. A rule-computed figure is
                      arithmetic; a model insight is an interpretation, and a
                      reader deciding whether to act on it needs to know which
                      one they are looking at. */}
                  {insight.source === 'MODEL' && (
                    <Chip
                      icon={<AutoAwesome sx={{ fontSize: 11 }} />}
                      label="AI analysis"
                      size="small"
                      sx={{
                        height: 18,
                        mb: 0.625,
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        color: 'primary.main',
                        backgroundColor: 'primary.main',
                        bgcolor: (theme) => `${theme.palette.primary.main}14`,
                        '& .MuiChip-icon': { color: 'primary.main', ml: 0.5 },
                      }}
                    />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                    {insight.title}
                  </Typography>
                  {insight.body && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {insight.body}
                    </Typography>
                  )}

                  {/* The numbers behind the claim, shown not hidden. */}
                  {evidence && Object.keys(evidence).length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.875 }}>
                      {Object.entries(evidence)
                        .slice(0, 3)
                        .map(([k, v]) => (
                          <Typography
                            key={k}
                            variant="caption"
                            sx={{
                              color: 'text.disabled',
                              fontFamily: 'monospace',
                              fontSize: '0.6875rem',
                            }}
                          >
                            {k}: {String(v)}
                          </Typography>
                        ))}
                    </Box>
                  )}

                  {insight.link && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.75 }}>
                      <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
                        View
                      </Typography>
                      <ChevronRight sx={{ fontSize: 14, color }} />
                    </Box>
                  )}
                </Box>
              </Box>

              <Tooltip title="Dismiss">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    dismiss.mutate(insight.id);
                  }}
                  sx={{ position: 'absolute', top: 4, right: 4 }}
                >
                  <Close sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </Box>
    </Collapse>
  );
};

export default InsightStrip;
